(() => {
  const controllers = new WeakMap();
  const isListingKey = (key) => key.startsWith('filter.') || key === 'sort_by' || key === 'page';
  const copyListingParams = (from, to) => {
    for (const [key, value] of from) {
      if (isListingKey(key) && value.trim() !== '') to.append(key, value);
    }
  };
  const enhance = (root) => {
    if (!root || root.hasAttribute('data-pcbc-enhanced')) return;
    const controller = new AbortController();
    const signal = controller.signal;
    controllers.set(root, controller);
    const collection = new URL(root.dataset.pcbcCollectionUrl, window.location.href);
    const view = root.dataset.pcbcView;
    const mobile = window.matchMedia('(max-width: 799px)');
    const status = root.querySelector('[data-pcbc-status]');
    const message = root.querySelector('[data-pcbc-status-message]');
    const retry = root.querySelector('[data-pcbc-retry]');
    let activeUrl = new URL(window.location.href);
    let requestController;
    let requestNumber = 0;
    let lastAttempt;

    const withinCollection = (url) => url.origin === collection.origin && url.pathname === collection.pathname;
    const syncDrawer = () => {
      const drawer = root.querySelector('.pcbc-filters');
      if (drawer) drawer.open = !mobile.matches;
    };
    const setStatus = (text, failed = false) => {
      if (status) status.hidden = !text;
      if (message) message.textContent = text;
      if (retry) retry.hidden = !failed;
    };
    const withContext = (url) => {
      const destination = new URL(url, activeUrl);
      destination.hash = '';
      destination.searchParams.delete('section_id');
      destination.searchParams.delete('sections');
      destination.searchParams.set('view', view);
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) destination.searchParams.set('preview_theme_id', previewTheme);
      return destination;
    };
    const visibleUrl = (destination) => {
      const visible = new URL(window.location.href);
      visible.hash = '';
      for (const key of [...visible.searchParams.keys()]) {
        if (isListingKey(key) || key === 'section_id' || key === 'sections') visible.searchParams.delete(key);
      }
      copyListingParams(destination.searchParams, visible.searchParams);
      return visible;
    };
    const fetchResults = async (destination, requestSignal) => {
      const read = async (url) => {
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: requestSignal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) return null;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        return parsed.querySelector('.pcbc-results');
      };
      const sectionUrl = new URL(destination);
      if (root.dataset.pcbcSectionId) sectionUrl.searchParams.set('section_id', root.dataset.pcbcSectionId);
      let results = await read(sectionUrl);
      if (!results && sectionUrl.searchParams.has('section_id')) results = await read(destination);
      if (!results) throw new Error('Missing collection results');
      return results;
    };
    const navigate = async (url, options = {}) => {
      const destination = withContext(url);
      if (!withinCollection(destination)) return;
      requestController?.abort();
      requestController = new AbortController();
      const currentRequest = requestController;
      const number = ++requestNumber;
      const timeout = window.setTimeout(() => currentRequest.abort(), 20000);
      lastAttempt = { url: destination.href, options };
      root.querySelector('.pcbc-results')?.setAttribute('aria-busy', 'true');
      setStatus(root.dataset.pcbcLoadingMessage);
      try {
        const incoming = await fetchResults(destination, currentRequest.signal);
        if (signal.aborted || number !== requestNumber) return;
        const results = document.importNode(incoming, true);
        root.querySelector('.pcbc-results').replaceWith(results);
        activeUrl = destination;
        syncDrawer();
        setStatus('');
        if (options.history !== 'none') {
          const visible = visibleUrl(destination);
          if (visible.href !== window.location.href) {
            window.history.pushState({ ...(window.history.state || {}), pcbc: true }, '', visible.href);
          }
        }
        if (options.scroll) {
          results.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
        if (options.focus) {
          const heading = results.querySelector('h2');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (signal.aborted || number !== requestNumber) return;
        setStatus(root.dataset.pcbcErrorMessage, true);
      } finally {
        window.clearTimeout(timeout);
        if (number === requestNumber) root.querySelector('.pcbc-results')?.removeAttribute('aria-busy');
      }
    };

    syncDrawer();
    mobile.addEventListener('change', syncDrawer, { signal });
    signal.addEventListener('abort', () => requestController?.abort(), { once: true });
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-pcbc-retry]')) {
        if (lastAttempt) navigate(lastAttempt.url, lastAttempt.options);
        return;
      }
      const link = event.target.closest('[data-pcbc-result-link]');
      if (!link || !root.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.getAttribute('href') || link.href, activeUrl);
      if (!withinCollection(destination)) return;
      event.preventDefault();
      navigate(destination, { scroll: true, focus: true });
    }, { signal });
    root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-pcbc-filter-form]');
      if (!form || !root.contains(form)) return;
      const destination = new URL(form.action, window.location.href);
      if (!withinCollection(destination)) return;
      event.preventDefault();
      destination.search = '';
      copyListingParams(new FormData(form), destination.searchParams);
      navigate(destination, { scroll: true, focus: true });
    }, { signal });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-pcbc-sort]')) event.target.closest('form')?.requestSubmit();
    }, { signal });
    root.addEventListener('toggle', (event) => {
      const facet = event.target;
      if (!facet.matches('.pcbc-facet') || !facet.open || mobile.matches) return;
      root.querySelectorAll('.pcbc-facet').forEach((other) => {
        if (other !== facet) other.open = false;
      });
      const panel = facet.querySelector('.pcbc-facet__panel');
      if (panel) {
        panel.style.left = '0';
        panel.style.right = 'auto';
        if (panel.getBoundingClientRect().right > window.innerWidth - 16) {
          panel.style.left = 'auto';
          panel.style.right = '0';
        }
      }
    }, { signal, capture: true });
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const focused = document.activeElement?.closest('details');
      const drawer = root.querySelector('.pcbc-filters');
      root.querySelectorAll('details[open]').forEach((detail) => {
        if (detail !== drawer || mobile.matches) detail.open = false;
      });
      focused?.querySelector('summary')?.focus();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (event.target.closest('.pcbc-toolbar')) return;
      root.querySelectorAll('.pcbc-facet').forEach((facet) => { facet.open = false; });
      if (mobile.matches) {
        const drawer = root.querySelector('.pcbc-filters');
        if (drawer) drawer.open = false;
      }
    }, { signal });
    window.addEventListener('popstate', () => navigate(window.location.href, { history: 'none' }), { signal });
    root.setAttribute('data-pcbc-enhanced', '');
  };
  document.querySelectorAll('[data-pcbc-section]').forEach(enhance);
  document.addEventListener('shopify:section:load', (event) => enhance(event.target.querySelector('[data-pcbc-section]')));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-pcbc-section]');
    if (root) controllers.get(root)?.abort();
  });
})();
